import { z } from 'zod';
import { ASSIGNMENT_STATUSES, AUDIENCE_WHERE, AUDIENCE_WHO, RC_AREAS, REGIONS, ROLES, SKILLS, TASK_AREAS, TASK_FREQS, TASK_STATUSES, TASK_TIMES, } from './domain.js';
/* ------------------------------------------------------------ primitives */
/**
 * Emails are stored and compared lowercased and trimmed — the invite flow
 * looks a user up by what a coordinator typed into a spreadsheet, which is
 * never consistently cased.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .email();
export const passwordSchema = z
    .string()
    .min(10, 'Use at least 10 characters.')
    .max(200, 'That is longer than we can store.');
export const nameSchema = z.string().trim().min(1).max(120);
export const idSchema = z.string().min(1).max(64);
export const roleSchema = z.enum(ROLES);
export const regionSchema = z.enum(REGIONS);
export const rcAreaSchema = z.enum(RC_AREAS);
export const skillSchema = z.enum(SKILLS);
/**
 * Any valid IANA zone, not just the picker's list — every "today", streak and
 * weekly window in the app is computed in the city's zone, so an invalid one
 * would corrupt all of them quietly.
 */
export const timezoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Not a timezone we recognise — use an IANA name like America/Denver.' });
/* ----------------------------------------------------------------- auth */
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const acceptInviteRequestSchema = z.object({
    token: z.string().min(20).max(200),
    name: nameSchema,
    password: passwordSchema,
    /** Volunteers pick these on the way in; coordinators may leave them empty. */
    skills: z.array(skillSchema).max(SKILLS.length).default([]),
    /** "Other" — a skill the fixed list doesn't have. Free text, optional. */
    otherSkills: z.string().trim().max(200).default(''),
    learn: z.array(skillSchema).max(SKILLS.length).default([]),
    /** Honest hours, in minutes per week. 10 minutes a day is a real answer. */
    weeklyMinutes: z.number().int().min(0).max(60 * 40).default(180),
    phone: z.string().trim().max(40).optional(),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(20).max(200),
    newPassword: passwordSchema,
});
/* --------------------------------------------------------------- invites */
export const createInviteRequestSchema = z.object({
    email: emailSchema,
    name: nameSchema.optional(),
    role: roleSchema,
    cityId: idSchema.optional(),
    rcArea: rcAreaSchema.optional(),
    region: regionSchema.optional(),
    note: z.string().trim().max(500).optional(),
});
/**
 * Spreadsheet import. The client parses the file (CSV or XLSX) into rows and
 * posts them as JSON, so the server never has to grow a spreadsheet parser
 * and the coordinator gets to see and fix the mapping before anything is sent.
 */
export const importRowSchema = z.object({
    email: z.string().trim().max(254),
    /**
     * The 1-based line in the original file (header included), so a
     * validation message points at the row the coordinator actually sees in
     * their spreadsheet — not at our index after filtering.
     */
    sourceRow: z.number().int().min(1).optional(),
    name: z.string().trim().max(120).optional().default(''),
    city: z.string().trim().max(120).optional().default(''),
    skills: z.string().trim().max(400).optional().default(''),
    phone: z.string().trim().max(40).optional().default(''),
    /** Optional. Blank means the default role (Volunteer). See parseImportRole. */
    role: z.string().trim().max(60).optional().default(''),
    /** Only for regional coordinators, who belong to an RC area rather than a city. */
    area: z.string().trim().max(120).optional().default(''),
});
export const importRequestSchema = z.object({
    rows: z.array(importRowSchema).min(1).max(2000),
    role: roleSchema.default('volunteer'),
    /** Preview first: validate and report, write nothing. */
    dryRun: z.boolean().default(true),
});
export const importRowResultSchema = z.object({
    row: z.number().int(),
    email: z.string(),
    outcome: z.enum([
        'invited',
        'already_member',
        'already_invited',
        'invalid_email',
        'unknown_city',
        'unknown_role',
        'unknown_area',
        'not_permitted',
    ]),
    detail: z.string().optional(),
    name: z.string().optional(),
    /**
     * The invitation link, returned for every row actually invited.
     *
     * This is the only moment the token exists in plaintext — it is stored
     * hashed and cannot be recovered afterwards. Without returning it here, an
     * import of 600 volunteers with no SMTP configured would report 600
     * successes and hand back nothing anyone could send.
     */
    link: z.string().optional(),
    delivery: z.enum(['sent', 'not_configured', 'failed']).optional(),
});
export const importResponseSchema = z.object({
    dryRun: z.boolean(),
    results: z.array(importRowResultSchema),
    summary: z.object({
        invited: z.number().int(),
        skipped: z.number().int(),
        failed: z.number().int(),
    }),
});
/* ----------------------------------------------------------------- tasks */
export const audienceSchema = z
    .object({
    where: z.enum(AUDIENCE_WHERE),
    /** Required when where is 'city' or 'cities'. */
    cityIds: z.array(idSchema).max(60).default([]),
    /** Required when where is 'rc_area'. */
    rcArea: rcAreaSchema.optional(),
    /** Required when where is 'region'. */
    region: regionSchema.optional(),
    who: z.enum(AUDIENCE_WHO),
    /** Required when who is 'skill'. */
    skills: z.array(skillSchema).max(SKILLS.length).default([]),
    /** Required when who is 'handpick' or 'one'. */
    userIds: z.array(idSchema).max(200).default([]),
})
    .superRefine((a, ctx) => {
    const need = (cond, path, message) => {
        if (!cond)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    };
    if (a.where === 'city')
        need(a.cityIds.length === 1, 'cityIds', 'Pick exactly one city.');
    if (a.where === 'cities')
        need(a.cityIds.length >= 1, 'cityIds', 'Pick at least one city.');
    if (a.where === 'rc_area')
        need(!!a.rcArea, 'rcArea', 'Pick an area.');
    if (a.where === 'region')
        need(!!a.region, 'region', 'Pick a region.');
    if (a.who === 'skill')
        need(a.skills.length >= 1, 'skills', 'Pick at least one skill.');
    if (a.who === 'handpick')
        need(a.userIds.length >= 1, 'userIds', 'Pick at least one person.');
    if (a.who === 'one')
        need(a.userIds.length === 1, 'userIds', 'Pick exactly one person.');
})
    /**
     * Drop every field the chosen mode does not use.
     *
     * Without this an audience can carry a city list that the matching SQL
     * ignores but the *visibility* checks read — so an RC could publish
     * `where: 'rc_area'` while smuggling Boston into `cityIds`, and a Boston
     * coordinator would then see, read the takers of, and close a task that
     * only ever reached West volunteers. One shape in, one shape stored.
     */
    .transform((a) => ({
    where: a.where,
    cityIds: a.where === 'city' || a.where === 'cities' ? a.cityIds : [],
    rcArea: a.where === 'rc_area' ? a.rcArea : undefined,
    region: a.where === 'region' ? a.region : undefined,
    who: a.who,
    skills: a.who === 'skill' ? a.skills : [],
    userIds: a.who === 'handpick' || a.who === 'one' ? a.userIds : [],
}));
export const createTaskRequestSchema = z.object({
    title: z.string().trim().min(3).max(160),
    /** Why it matters — the line that gets someone to say yes. */
    why: z.string().trim().max(500).default(''),
    detail: z.string().trim().max(4000).default(''),
    steps: z.array(z.string().trim().min(1).max(300)).max(20).default([]),
    area: z.enum(TASK_AREAS),
    time: z.enum(TASK_TIMES),
    freq: z.enum(TASK_FREQS),
    /** ISO date (YYYY-MM-DD). Null means "any day — whenever fits". */
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
    /** How many volunteers are wanted. 0 means no cap. */
    needed: z.number().int().min(0).max(500).default(1),
    audience: audienceSchema,
    status: z.enum(['draft', 'open']).default('open'),
});
export const taskActionRequestSchema = z.object({
    action: z.enum(['accept', 'decline', 'reconsider', 'complete', 'withdraw']),
    /** Free text on decline ("can't make it") or completion proof. */
    note: z.string().trim().max(1000).optional(),
    /** A link or filename offered as proof of completion. */
    proof: z.string().trim().max(500).optional(),
    /** An uploaded photo offered as proof of completion. */
    proofFileId: z.string().min(1).max(64).optional(),
});
export const taskListQuerySchema = z.object({
    area: z.enum(TASK_AREAS).optional(),
    status: z.enum(TASK_STATUSES).optional(),
    /** Volunteer view: only what is still open and matches me. */
    mine: z.coerce.boolean().optional(),
    cityId: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/* ------------------------------------------------------------ responses */
export const cityDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    rcArea: rcAreaSchema,
    region: regionSchema,
    volunteerCount: z.number().int(),
    hasCoordinator: z.boolean(),
});
/* ---------------------------------------------------- city management */
export const cityCreateSchema = z.object({
    name: z.string().trim().min(2).max(80),
    rcArea: rcAreaSchema,
    timezone: timezoneSchema,
});
export const cityUpdateSchema = z
    .object({
    name: z.string().trim().min(2).max(80),
    rcArea: rcAreaSchema,
    timezone: timezoneSchema,
    active: z.boolean(),
})
    .partial();
export const meDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: roleSchema,
    cityId: z.string().nullable(),
    cityName: z.string().nullable(),
    rcArea: rcAreaSchema.nullable(),
    region: regionSchema.nullable(),
    skills: z.array(skillSchema),
    otherSkills: z.string(),
    learn: z.array(skillSchema),
    weeklyMinutes: z.number().int(),
    /** Set while a volunteer has paused; ISO date they are back. */
    awayUntil: z.string().nullable(),
    /** Notification emails they have left on. */
    emailNewWork: z.boolean(),
    emailIdeaDecisions: z.boolean(),
    /** As it happens, or one email a day / a week. */
    emailFrequency: z.enum(['instant', 'daily', 'weekly']),
});
export const taskDtoSchema = z.object({
    id: z.string(),
    title: z.string(),
    why: z.string(),
    detail: z.string(),
    steps: z.array(z.string()),
    area: z.enum(TASK_AREAS),
    areaName: z.string(),
    time: z.enum(TASK_TIMES),
    freq: z.enum(TASK_FREQS),
    dueDate: z.string().nullable(),
    needed: z.number().int(),
    status: z.enum(TASK_STATUSES),
    cityName: z.string().nullable(),
    createdByName: z.string(),
    createdAt: z.string(),
    /** How many have said yes, and how many have finished. */
    acceptedCount: z.number().int(),
    completedCount: z.number().int(),
    /** This viewer's own relationship to the task, if any. */
    myStatus: z.enum(ASSIGNMENT_STATUSES).nullable(),
});
export const inviteDtoSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    role: roleSchema,
    cityName: z.string().nullable(),
    status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
    /** Only returned to the coordinator who may send it; absent once accepted. */
    link: z.string().nullable(),
    invitedByName: z.string(),
    createdAt: z.string(),
    expiresAt: z.string(),
});
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        /** Field-level problems, keyed by field path. */
        fields: z.record(z.string()).optional(),
    }),
});
//# sourceMappingURL=schemas.js.map